import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * OfferDetails_Desc_3 Component
 * Displays an offer details section with:
 * - Introductory message
 * - Promo code (prominently displayed)
 * - Urgency message
 * - CTA button
 * - Closing text
 * - Signature
 * - Postscript
 * Centered layout with clean, simple design
 */
const OfferDetailsDesc3 = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    introMessage = "",
    promoCode = "",
    urgencyMessage = "",
    buttonText = "",
    buttonLink = "#",
    closingText = "",
    signature = "",
    postscript = "",
    component_name = "OfferDetails_Desc_3",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localIntroMessage, setLocalIntroMessage] = useState(introMessage);
  const [localPromoCode, setLocalPromoCode] = useState(promoCode);
  const [localUrgencyMessage, setLocalUrgencyMessage] = useState(urgencyMessage);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);
  const [localClosingText, setLocalClosingText] = useState(closingText);
  const [localSignature, setLocalSignature] = useState(signature);
  const [localPostscript, setLocalPostscript] = useState(postscript);

  useEffect(() => {
    setLocalIntroMessage(introMessage || "");
    setLocalPromoCode(promoCode || "");
    setLocalUrgencyMessage(urgencyMessage || "");
    setLocalButtonText(buttonText || "");
    setLocalButtonLink(buttonLink || "#");
    setLocalClosingText(closingText || "");
    setLocalSignature(signature || "");
    setLocalPostscript(postscript || "");
  }, [id, introMessage, promoCode, urgencyMessage, buttonText, buttonLink, closingText, signature, postscript]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      introMessage: localIntroMessage,
      promoCode: localPromoCode,
      urgencyMessage: localUrgencyMessage,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
      closingText: localClosingText,
      signature: localSignature,
      postscript: localPostscript,
    });
    setIsEditing(false);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component offer-details-desc-3-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Offer Details with Promo Code</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Introductory Message</label>
          <input
            className="form-control"
            value={localIntroMessage}
            onChange={(e) => setLocalIntroMessage(e.target.value)}
            placeholder="Enter introductory message"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Promo Code</label>
          <input
            className="form-control"
            value={localPromoCode}
            onChange={(e) => setLocalPromoCode(e.target.value)}
            placeholder="Enter promo code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Urgency Message</label>
          <input
            className="form-control"
            value={localUrgencyMessage}
            onChange={(e) => setLocalUrgencyMessage(e.target.value)}
            placeholder="Enter urgency/expiration message"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Text</label>
          <input
            className="form-control"
            value={localButtonText}
            onChange={(e) => setLocalButtonText(e.target.value)}
            placeholder="Enter button text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Link</label>
          <input
            className="form-control"
            value={localButtonLink}
            onChange={(e) => setLocalButtonLink(e.target.value)}
            placeholder="Enter button link URL"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Closing Text</label>
          <input
            className="form-control"
            value={localClosingText}
            onChange={(e) => setLocalClosingText(e.target.value)}
            placeholder="Enter closing text (e.g., Enjoy!)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Signature</label>
          <input
            className="form-control"
            value={localSignature}
            onChange={(e) => setLocalSignature(e.target.value)}
            placeholder="Enter signature"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Postscript (P.S.)</label>
          <textarea
            className="form-control"
            value={localPostscript}
            onChange={(e) => setLocalPostscript(e.target.value)}
            placeholder="Enter postscript text"
            rows={2}
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  return (
    <>
      {!previewMode && (
        <div className="email-component offer-details-desc-3 mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tbody>
          <tr>
            <td
              align="center"
              style={{
                padding: "32px 16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tbody>
                  {/* Introductory Message */}
                  {localIntroMessage && (
                    <tr>
                      <td
                        align="left"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "left",
                          }}
                        >
                          {localIntroMessage}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Promo Code */}
                  {localPromoCode && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "12px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            letterSpacing: "2px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localPromoCode}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Urgency Message */}
                  {localUrgencyMessage && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "24px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localUrgencyMessage}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* CTA Button */}
                  {localButtonText && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{ display: "inline-block" }}
                        >
                          <tr>
                            <td
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                border: `2px solid ${secondaryColor || "#a1894c"}`,
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href={localButtonLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  padding: "12px 24px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: primaryColor || "#333333",
                                  textDecoration: "none",
                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                }}
                              >
                                {localButtonText}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Closing Text */}
                  {localClosingText && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "12px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localClosingText}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Signature */}
                  {localSignature && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "24px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localSignature}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Postscript */}
                  {localPostscript && (
                    <tr>
                      <td
                        align="left"
                        style={{
                          paddingTop: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "left",
                            fontStyle: "italic",
                          }}
                        >
                          {localPostscript}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default OfferDetailsDesc3;

